import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  TrendingUp,
  DollarSign,
  FileText,
  Eye,
  ShoppingCart,
  Receipt,
  Wallet,
  CreditCard,
  Ban,
  Gift,
  RotateCcw,
  Minus,
  ArrowDownCircle,
  Plus,
  Users,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  dayend_report,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import * as FileSaver from "file-saver";
import XLSX from "sheetjs-style";

export default function DayEndReport() {
  const { toast } = useToast();

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(new Date());
  const [filterOption, setFilterOption] = useState("");

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [dayReportData, setDayReportData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [stats, setStats] = useState({
    totalReports: 0,
    totalClosingBalance: 0,
    averageBalance: 0,
  });

  const fetchReportData = useCallback(
    async (start_date, end_date, flag = "1", call_id = "0") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay())
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startDate || !endDate) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", start_date.toISOString().split("T")[0]);
      fd.append("end_date", end_date.toISOString().split("T")[0]);
      fd.append("flag", flag);
      fd.append("call_id", call_id);

      try {
        const response = await server_post_data(dayend_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const data = response.data.message.day_end_data || [];
          setDayReportData(data);

          const totalBalance = data.reduce(
            (sum, item) => sum + parseFloat(item.closing_balance || 0),
            0
          );
          setStats({
            totalReports: data.length,
            totalClosingBalance: totalBalance,
            averageBalance: data.length > 0 ? totalBalance / data.length : 0,
          });
        }
      } catch (error) {
        console.error("Error fetching report:", error);
        toast({
          title: "Error",
          description: "Failed to load report data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startDate, endDate, toast]
  );

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchReportData(startDate, endDate, "2", "0");
  }, [startDate, endDate, fetchReportData]);

  const exportToExcel = useCallback(() => {
    if (!dayReportData.length) {
      toast({ title: "Error", description: "No data available for export!", variant: "destructive" });
      return;
    }
    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const ws = XLSX.utils.json_to_sheet(dayReportData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, "day_end_report.xlsx");
    toast({ title: "Success", description: "Report exported successfully" });
  }, [dayReportData, toast]);

  const totalPages = Math.ceil(dayReportData.length / entriesPerPage);
  const currentData = dayReportData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    fetchReportData(startDate, endDate, "1", "0");
  }, []);

  // ✅ DYNAMIC: store raw row directly, no mapping
  const handleViewReport = (row) => {
    setSelectedReport(row);
    setIsSidebarOpen(true);
  };

  return (
    <MainLayout>
      <Header
        title="Day End Report"
        subtitle="View and manage daily closing balance reports"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.totalReports}</p>
                <p className="text-sm text-muted-foreground">Total Reports</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.totalClosingBalance.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Total Closing Balance</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{stats.averageBalance.toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground">Average Balance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "dd MMM yyyy") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "dd MMM yyyy") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <div className={cn("flex items-end gap-2", filterOption !== "custom" && "md:col-span-3")}>
                <Button onClick={handleSearch} className="gap-2" disabled={showLoaderAdmin}>
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={exportToExcel}
                  className="gap-2"
                  disabled={dayReportData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Created Date</TableHead>
                <TableHead className="text-muted-foreground text-center">No. of Orders</TableHead>
                <TableHead className="text-muted-foreground text-right">Closing Balance (₹)</TableHead>
                <TableHead className="text-muted-foreground text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showLoaderAdmin ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : currentData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">No data found</TableCell>
                </TableRow>
              ) : (
                currentData.map((row, index) => (
                  <TableRow key={index} className="border-border">
                    <TableCell className="font-medium">{row.entry_date}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1">
                        <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                        {row.order_count || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-primary">
                      ₹{parseFloat(row.closing_balance || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" className="gap-2" onClick={() => handleViewReport(row)}>
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, dayReportData.length)} of{" "}
                {dayReportData.length} entries
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</Button>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (pageNumber === 1 || pageNumber === totalPages || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)) {
                    return (
                      <Button key={pageNumber} variant={pageNumber === currentPage ? "default" : "outline"} size="sm" onClick={() => handlePageChange(pageNumber)}>
                        {pageNumber}
                      </Button>
                    );
                  } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Day End Summary Sidebar */}
      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Day End Summary</SheetTitle>
          </SheetHeader>

          {selectedReport && (
            <div className="mt-6 space-y-6">
              {/* Header Info */}
              <Card>
                <CardContent className="pt-6 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Date</span>
                    <span className="font-semibold">{selectedReport.entry_date}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4" />
                      Order Count
                    </span>
                    <span className="text-xl font-bold text-primary">{selectedReport.order_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Total Customers (Pax)
                    </span>
                    <span className="text-xl font-bold text-primary">{selectedReport.total_pax || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Closing Balance</span>
                    <span className="text-xl font-bold text-primary">₹{parseFloat(selectedReport.closing_balance || 0).toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Sales Breakdown */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  SALES BREAKDOWN
                </h3>
                <Card>
                  <CardContent className="pt-6 space-y-2.5">
                    <SummaryRow label="Sub Total" value={selectedReport.sub_total} bold />
                    <SummaryRow label="Discount" value={selectedReport.discount} negative />
                    <SummaryRow label="Delivery Charge" value={selectedReport.delivery_charge} />
                    <SummaryRow label="Container Charge" value={selectedReport.container_charge} />
                    <SummaryRow label="Service Charge" value={selectedReport.service_charge} />
                    <SummaryRow label="Tax" value={selectedReport.tax} />
                    <SummaryRow label="Round Off" value={selectedReport.round_off} />
                    <SummaryRow label="Waived Off" value={selectedReport.waived_off} negative />
                    <Separator className="my-3" />
                    <SummaryRow label="Grand Total" value={selectedReport.grand_total} highlight />
                    <SummaryRow label="Net Sales" value={selectedReport.net_sales} highlight />
                  </CardContent>
                </Card>
              </div>

              {/* Payment Methods */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <Wallet className="w-4 h-4" />
                  PAYMENT BREAKDOWN
                </h3>
                <Card>
                  <CardContent className="pt-6 space-y-2.5">
                    <SummaryRow label="Cash" value={selectedReport.cash} icon={<DollarSign className="w-4 h-4" />} />
                    <SummaryRow label="Card" value={selectedReport.card} icon={<CreditCard className="w-4 h-4" />} />
                    <SummaryRow label="Wallet" value={selectedReport.wallet} icon={<Wallet className="w-4 h-4" />} />
                    <SummaryRow label="Online" value={selectedReport.online} icon={<Receipt className="w-4 h-4" />} />
                    <SummaryRow label="Other" value={selectedReport.other} icon={<FileText className="w-4 h-4" />} />
                    <SummaryRow label="Not Paid" value={selectedReport.not_paid} warning={parseFloat(selectedReport.not_paid) > 0} />
                    <SummaryRow label="Due" value={selectedReport.due} warning={parseFloat(selectedReport.due) > 0} />
                  </CardContent>
                </Card>
              </div>

              {/* Adjustments */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" />
                  ADJUSTMENTS & OTHERS
                </h3>
                <Card>
                  <CardContent className="pt-6 space-y-2.5">
                    <SummaryRow label="Cancel" value={selectedReport.cancel} icon={<Ban className="w-4 h-4" />} />
                    <SummaryRow label="Complimentary" value={selectedReport.complimentary} icon={<Gift className="w-4 h-4" />} />
                    <SummaryRow label="Sales Return" value={selectedReport.sales_return} icon={<RotateCcw className="w-4 h-4" />} />
                    <SummaryRow label="Expenses" value={selectedReport.expenses} icon={<Minus className="w-4 h-4" />} negative />
                    <SummaryRow label="Withdrawal" value={selectedReport.withdrawal} icon={<ArrowDownCircle className="w-4 h-4" />} negative />
                    <SummaryRow label="Cash Top-Up" value={selectedReport.cash_topup} icon={<Plus className="w-4 h-4" />} />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </MainLayout>
  );
}

function SummaryRow({ label, value, bold = false, highlight = false, negative = false, warning = false, icon = null }) {
  const formattedValue = typeof value === "number" ? value.toFixed(2) : (parseFloat(value) || 0).toFixed(2);

  return (
    <div className={cn("flex justify-between items-center py-1", highlight && "bg-primary/5 px-3 -mx-3 rounded-lg py-2")}>
      <span className={cn("text-sm flex items-center gap-2", bold && "font-semibold", highlight && "font-semibold text-foreground", !highlight && "text-muted-foreground")}>
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {label}
      </span>
      <span className={cn("font-medium", bold && "font-semibold", highlight && "text-lg font-bold text-primary", negative && "text-destructive", warning && value > 0 && "text-warning")}>
        ₹{formattedValue}
      </span>
    </div>
  );
}